/**
 * Droit de reprise du maire — refonte UX, chantier 4 (maquette 12).
 *
 * Le maire est seul chargé de l'administration (CGCT L2122-18) : il peut reprendre
 * à tout moment l'administration complète. Immédiat, motif obligatoire, inscrit au
 * journal, administrateurs complets prévenus. Une fois repris, il peut revenir au
 * profil Maire. Ne s'affiche que pour la personne au profil « Maire ».
 */
import { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Card, Button, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException, type ProfilGouvDTO } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

export function RepriseMairePanel() {
  const { user } = useAuth();
  const [moi, setMoi] = useState<ProfilGouvDTO | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);

  const charger = () => api.gouvernance.profils()
    .then((r) => setMoi(r.profils.find((p) => p.id === user?.id) ?? null)).catch(() => setMoi(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void charger(); }, [user?.id]);

  if (!moi || moi.profil !== 'maire') return null;

  async function reprendre() {
    setMsg(null);
    try {
      const r = await api.gouvernance.reprise(motif.trim());
      setOuvert(false); setMotif('');
      setMsg({ ok: true, t: `Vous administrez la commune. ${r.prevenus} administrateur(s) prévenu(s).` });
      await charger();
    } catch (e) { setMsg({ ok: false, t: e instanceof ApiException ? e.payload.error : 'Reprise impossible' }); }
  }
  async function revenir() {
    setMsg(null);
    try { await api.gouvernance.retourProfilMaire(); setMsg({ ok: true, t: 'Vous êtes revenu au profil Maire.' }); await charger(); }
    catch (e) { setMsg({ ok: false, t: e instanceof ApiException ? e.payload.error : 'Retour impossible' }); }
  }

  return (
    <Card variant="glass" padded className="mt-4 border-warning/40">
      <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-ink"><ShieldAlert className="h-4 w-4" aria-hidden="true" /> Droit de reprise</h2>
      <p className="text-xs text-ink-muted">
        {moi.adminComplet
          ? 'Vous administrez actuellement la commune vous-même.'
          : 'La commune est administrée par votre administrateur complet. Vous pouvez reprendre la main à tout moment : c’est immédiat, inscrit au journal, et il en est prévenu.'}
      </p>
      {msg && <p className={`mt-2 text-xs ${msg.ok ? 'text-success' : 'text-urgent'}`} role={msg.ok ? 'status' : 'alert'}>{msg.t}</p>}
      <div className="mt-3">
        {moi.adminComplet
          ? <Button size="sm" variant="glass" onClick={() => void revenir()}>Revenir au profil Maire</Button>
          : <Button size="sm" variant="glass" onClick={() => setOuvert(true)}>Reprendre l’administration complète</Button>}
      </div>
      <Dialog open={ouvert} onClose={() => setOuvert(false)} title="Reprendre l’administration complète ?"
        footer={<><Button variant="ghost" onClick={() => setOuvert(false)}>Annuler</Button><Button variant="primary" disabled={motif.trim().length < 5} onClick={() => void reprendre()}>Reprendre la main</Button></>}>
        <div className="space-y-3 text-sm">
          <p>Vous êtes seul chargé de l’administration de la commune : vous pouvez reprendre la main à tout moment.</p>
          <ul className="list-disc space-y-1 pl-5 text-xs text-ink-muted">
            <li>Effet immédiat : accès complet à tous les services.</li>
            <li>Inscrit au journal d’audit, avec votre motif.</li>
            <li>Les administrateurs complets sont prévenus.</li>
          </ul>
          <label className="block text-xs text-ink">Motif
            <Input className="mt-1" value={motif} maxLength={500} placeholder="Ex : absence prolongée de la DGS" onChange={(e) => setMotif(e.target.value)} />
          </label>
        </div>
      </Dialog>
    </Card>
  );
}
